import { strings } from '@app/common/strings';
import { withPreventDoubleClick } from '@app/components/hoc';
import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { aiSummaryExpenseReducer } from '../../../screens/module-specific-utilities/pages/ai-summary/redux/selectors';
import { AI_Summary_Constants, AI_SUMMARY_KEYS, keyMap } from '@app/screens/module-specific-utilities/pages/ai-summary/utils/constants';

const DETAIL_GRID_COLUMNS = [
  { header: keyMap.table_id, key: AI_SUMMARY_KEYS.TABLE_ID },
  { header: keyMap.date, key: AI_SUMMARY_KEYS.DATE },
  { header: keyMap.division, key: AI_SUMMARY_KEYS.DIVISION },
  { header: keyMap.hqPool, key: AI_SUMMARY_KEYS.HQ_POOL },
  { header: keyMap.state, key: AI_SUMMARY_KEYS.STATE },
  { header: keyMap.staffName, key: AI_SUMMARY_KEYS.STAFF_NAME },
  { header: keyMap.staffEmployeeCode, key: AI_SUMMARY_KEYS.STAFF_EMPLOYEE_CODE },
  { header: keyMap.managername, key: AI_SUMMARY_KEYS.MANAGER_NAME },
  { header: keyMap.managercode, key: AI_SUMMARY_KEYS.MANAGER_CODE },
  { header: keyMap.expenseType, key: AI_SUMMARY_KEYS.EXPENSE_TYPE },
  { header: keyMap.standard, key: AI_SUMMARY_KEYS.STANDARD },
  { header: keyMap.claimed, key: AI_SUMMARY_KEYS.CLAIMED },
  { header: keyMap.aiDecision, key: AI_SUMMARY_KEYS.AI_DECISION },
  { header: keyMap.aiAmount, key: AI_SUMMARY_KEYS.AI_AMOUNT },
  { header: keyMap.aiSummary, key: AI_SUMMARY_KEYS.AI_SUMMARY },
  { header: keyMap.adminAmount, key: AI_SUMMARY_KEYS.ADMIN_AMOUNT },
  { header: keyMap.adminSummary, key: AI_SUMMARY_KEYS.ADMIN_SUMMARY },
  { header: keyMap.accuracy, key: AI_SUMMARY_KEYS.ACCURACY },
];

const CATEGORY_GRID_COLUMNS = [
  { header: 'Category', key: 'category' },
  { header: '# of cases', key: 'total_cases' },
  { header: 'Admin Amt', key: 'admin_amount_total' },
  { header: 'AI Amount', key: 'ai_amount_total' },
  { header: 'Approved # of cases', key: 'approved_cases' },
  { header: 'Approved % cases of total', key: 'approved_percentage' },
  { header: 'Approved Admin Amt', key: 'approved_admin_amount' },
  { header: 'Approved AI Amount', key: 'approved_ai_amount' },
  { header: 'Modified # of cases', key: 'modified_cases' },
  { header: 'Modified % cases of total', key: 'modified_percentage' },
  { header: 'Modified Admin Amt', key: 'modified_admin_amount' },
  { header: 'Modified AI Amount', key: 'modified_ai_amount' },
  { header: 'Review # of cases', key: 'review_cases' },
  { header: 'Review % cases of total', key: 'review_percentage' },
  { header: 'Review Admin Amt', key: 'review_admin_amount' },
  { header: 'Review AI Amount', key: 'review_ai_amount' },
];

export const AIExpenseSummaryNavBar = () => {
  const DebouncedBtn = withPreventDoubleClick(Button, 400);
  const aiSummaryExpenseCompleteData = useSelector(
    aiSummaryExpenseReducer?.getAISummaryExpenseCompleteData(),
  );

  const formatDate = (value: any) => {
    if (!value) {
      return '';
    }
    const parsedDate = new Date(value);
    if (isNaN(parsedDate.getTime())) {
      return value;
    }
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const year = parsedDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDownloadExcel = () => {
    if (!Array.isArray(aiSummaryExpenseCompleteData) || aiSummaryExpenseCompleteData.length === 0) {
      return;
    }

    const firstRow = aiSummaryExpenseCompleteData[0] || {};
    const selectedColumns =
      firstRow?.category !== undefined && firstRow?.total_cases !== undefined
        ? CATEGORY_GRID_COLUMNS
        : DETAIL_GRID_COLUMNS;

    const filteredData = aiSummaryExpenseCompleteData.map((row: any) => {
      const mappedRow: Record<string, any> = {};
      selectedColumns.forEach(column => {
        const rawValue = row?.[column.key];
        mappedRow[column.header] = column.key === AI_SUMMARY_KEYS.DATE ? formatDate(rawValue) : rawValue ?? '';
      });
      return mappedRow;
    });

    const headers = selectedColumns.map(column => column.header);
    const worksheet = XLSX.utils.json_to_sheet(filteredData, { header: headers });
    worksheet['!cols'] = headers.map(header => ({ wch: Math.max(15, header.length + 2) }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'AI Summary Expense Report');

    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `AI_Summary_Expense_Report_${currentDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };
  return (
    <Box sx={[styles.container]}>
      <Box sx={styles.flexSpaceBetween}>
        <Typography
          variant="h6"
          noWrap
          component="div"
          color={'#1c1939'}
          fontStyle={'normal'}
          marginLeft={1.2}>
          {AI_Summary_Constants.AI_SUMMARY_EXPENSE}
        </Typography>
     
      </Box>
       <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <DebouncedBtn
                      variant="contained" 
                      size="large" 
                      onClick={handleDownloadExcel} 
                      disabled={aiSummaryExpenseCompleteData?.length === 0}
                      sx={{ minWidth: '140px' }}
                    > 
                      {strings.DownloadExcel} 
                    </DebouncedBtn> 
                  </Box>
    </Box>
  );
};


const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height:80,
    marginTop:3.5,
  },
  flexDisplay: {
    display: 'flex',
  },
  flexSpaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginBottom: '5px',
  },
  antSwitch: {
    marginTop: '4px !important',
  },
  formControl: {
    margin: '20px',
    marginLeft: '0px',
    marginRight: '0px',
  },
  QtyformContainer: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  QtyformControl: {
    margin: '12px',
    backgroundColor: '#ffffff',
    borderWidth: 0.7,
    borderColor: '#322b7c',
  },
  Modal: {
    padding: '20px 0px',
  },
};
